<template>
  <div class="editor-tarjeta flex w-full text-left">
    <!-- Columna izquierda: formulario -->
    <div class="editor-form flex flex-column gap-5 overflow-auto p-4">
      <div class="flex gap-2">
        <Button label="Guardar" data-e2e="btn-guardar-territorio" @click="guardar" />
        <Button label="Cancelar" data-e2e="btn-cancelar-territorio" severity="secondary" @click="cancel" />
      </div>

      <!-- Nombre y detalles -->
      <section>
        <h3 class="section-title">Asignar nombre y detalles</h3>
        <TarjetaTerritorio />
        <TarjetaHelp :step="0" />
      </section>

      <!-- Límites -->
      <section>
        <h3 class="section-title">Marcar límites en el mapa</h3>
        <Button
          :label="step === 1 ? 'Marcando límites…' : 'Marcar límites'"
          :icon="step === 1 ? 'pi pi-check' : 'pi pi-pencil'"
          :outlined="step !== 1"
          @click="activarModo(1, 'Marcar límites')"
        />
        <TarjetaHelp :step="1" show />
      </section>

      <!-- Manzanas -->
      <section v-if="terr.tipo === 'default'">
        <h3 class="section-title">Definir manzanas</h3>
        <Button
          :label="step === 2 ? 'Definiendo manzanas…' : 'Definir manzanas'"
          :icon="step === 2 ? 'pi pi-check' : 'pi pi-pencil'"
          :outlined="step !== 2"
          @click="activarModo(2, 'Definir centro y manzanas')"
        />
        <TarjetaHelp :step="2" show />
      </section>

      <!-- Puntos de encuentro -->
      <section>
        <h3 class="section-title">Definir puntos de encuentro</h3>
        <Button
          :label="step === 3 ? 'Definiendo puntos…' : 'Definir puntos de encuentro'"
          :icon="step === 3 ? 'pi pi-check' : 'pi pi-pencil'"
          :outlined="step !== 3"
          @click="activarModo(3, 'Definir puntos de encuentro')"
        />
        <TarjetaHelp :step="3" show />

        <div class="flex flex-column-reverse gap-2 mt-3" v-if="terr.puntoEncuentro.length">
          <div
            v-for="(item, index) in terr.puntoEncuentro"
            :key="index"
          >
            <div class="flex gap-2 justify-content-between align-items-end">
              <label class="text-xs">Punto de Encuentro {{ index + 1 }} </label>
              <Button
                icon="pi pi-trash"
                size="small"
                text
                severity="secondary"
                @click="removePE(index)"
              />
            </div>
            <div class="flex flex-column gap-1 pt-1">
              <InputText placeholder="Dirección" v-model="item.direccion" />
              <InputText
                placeholder="Nombre descriptivo (opcional)"
                v-model="item.name"
                class="text-xs"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Características -->
      <section>
        <h3 class="section-title">Características del territorio</h3>
        <div class="flex flex-column gap-4">

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Tamaño de grupo</label>
            <InputNumber v-model="terr.tamanoGrupo" :min="1" :max="200" placeholder="Número de personas" class="w-10rem" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Apto durante lluvia</label>
            <SelectButton v-model="terr.aptoDuranteLluvia" :options="['Sí', 'No']" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Apto luego de lluvia</label>
            <SelectButton v-model="terr.aptoLuegoDeLluvia" :options="['Sí', 'No']" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Tipo de vehículo</label>
            <MultiSelect
              v-model="terr.tipoVehiculo"
              :options="tiposVehiculo"
              placeholder="Seleccionar tipos..."
              display="chip"
            />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Seguridad</label>
            <SelectButton v-model="terr.seguridad" :options="nivelesAMB" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Nivel de productividad</label>
            <SelectButton v-model="terr.nivelProductividad" :options="nivelesAMB" />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Publicadores cerca</label>
            <Textarea v-model="terr.publicadoresCerca" rows="2" autoResize placeholder="Nombres de publicadores cercanos..." />
          </div>

          <div class="flex flex-column gap-1">
            <label class="text-sm font-medium">Observaciones</label>
            <Textarea v-model="terr.observaciones" rows="3" autoResize placeholder="Notas adicionales..." />
          </div>

          <div class="flex flex-column gap-2">
            <label class="text-sm font-medium">Datos adicionales</label>
            <div class="flex gap-2 flex-wrap">
              <Button
                label="Estudios iniciados"
                :badge="terr.estudios.length > 0 ? String(terr.estudios.length) : undefined"
                outlined
                icon="pi pi-book"
                @click="route.$patch({ page: 'estudiosTerritorio', params: { id: terr.uuid } })"
              />
              <Button
                label="Transporte"
                :badge="terr.transporte.length > 0 ? String(terr.transporte.length) : undefined"
                outlined
                icon="pi pi-map-marker"
                @click="route.$patch({ page: 'transporteTerritorio', params: { id: terr.uuid } })"
              />
            </div>
          </div>

        </div>
      </section>

      <!-- Rutas -->
      <section v-if="terr.tipo === 'routes'">
        <h3 class="section-title">Seleccionar Rutas (Opcional)</h3>
        <RutasSelector v-model="terr.routes" />
        <TarjetaHelp :step="4" />
      </section>
    </div>

    <!-- Columna derecha: mapa -->
    <div class="editor-map flex flex-column">
      <div class="map-toolbar flex align-items-center justify-content-between px-3">
        <div class="text-sm">{{ titleModal || "Mapa del territorio" }}</div>
        <Button
          v-if="step >= 1 && step <= 3"
          icon="pi pi-trash"
          size="small"
          severity="secondary"
          @click="clear(step)"
        />
      </div>
      <MapaBase
        class="map flex-auto"
        ref="mapBase"
        height="100%"
        :terrID="terr.zona + terr.numero"
        :center="terr.center"
        :color="terr.color"
        @mapClick="onMapClick"
        @mapMoveend="() => onMapMoveend()"
        @zoomstart="zoomstart"
        @zoomend="zoomend"
        @ready="ready"
        @dragendMz="dragEndMz"
        @clickMz="removeMz"
        v-if="showMapCount"
      ></MapaBase>
    </div>
  </div>
</template>

<script setup>
import TarjetaHelp from "@/components/TarjetaTerritorio/TarjetaHelp.vue";
import RutasSelector from "@/page/rutas/RutasSelector.vue";
import { ref, onMounted, computed, reactive } from "vue";
import { useTerritorioStore } from "@/store/territorio";
// import { useMapStore } from "@/store/map";
import { useMzNumbers } from "@/store/map/mzNumber";
import { useRouterStore } from "@/store/router";
import { useSaveTerritorio } from "@/composables/commands/useSaveTerritorio";
import { useNominatim } from "@/composables/useNominatim";

const { reverseGeocode } = useNominatim();

const tiposVehiculo = ['A pie', 'Bicicleta', 'Auto', 'Moto', 'Moto cross', '4x4', 'Transporte público'];
const nivelesAMB = ['Alta', 'Media', 'Baja'];

const route = useRouterStore();
const terr = useTerritorioStore();
const { execute: saveTerritorio } = useSaveTerritorio();
const mapBase = ref(null);
const mzNumbers = useMzNumbers();
const step = ref(0);
const showMapCount = ref(false);
const titleModal = ref("");

const limitMarkers = [];
const manzanasMarkers = [];

const activarModo = (mode, title) => {
  // Alternar: si el modo ya está activo, se desactiva (mapa en modo solo lectura)
  if (step.value === mode) {
    step.value = 0;
    titleModal.value = "";
    return;
  }
  step.value = mode;
  titleModal.value = title;
};

const clear = (step) => {
  if (step == 1) clearLimits();
  if (step == 2) clearMzNumbers();
  if (step == 3) clearPuntoEncuentro();
};




const clearPuntoEncuentro = () => {
  terr.$patch({ puntoEncuentro: [] });
  renderPuntoEncuentro();
};

const removePE = (index) => {
  terr.$patch({ puntoEncuentro: terr.puntoEncuentro.filter((p, i) => i !== index) });
  renderPuntoEncuentro();
};

onMounted(() => {
  // El mapa es persistente a la derecha en modo escritorio
  showMapCount.value = true;
  step.value = 0;
});

const map = () => mapBase.value.m.map.value;
const m = () => mapBase.value.m;
// reactive({
//   map: mapBase.value.m.map.value,
//   L: mapBase.value.m.L,
//   m: mapBase.value.m,
// });


var polTerr;

const clearLimits = () => {
  // terr.limits.value = []
  terr.$patch({ limits: [] });
  if (polTerr) map().removeLayer(polTerr);
  polTerr = [];
  if (limitMarkers.length > 0) {
    for (let i in limitMarkers) {
      map().removeLayer(limitMarkers[i]);
    }
  }
};

const clearMzNumbers = () => {
  terr.$patch({ mzNumbers: [] });
  mzNumbers.$patch({ mzNumbers: [] });
};
const removeLastMz = () => {
  terr.$patch({ mzNumbers: terr.mzNumbers.slice(0, -1) });
  mzNumbers.$patch({ mzNumbers: mzNumbers.mzNumbers.slice(0, -1) });
};

const onMapClick = (e) => {
  if (step.value == 1) configureLimit(e);
  if (step.value == 2) configureMzNumber(e);
  if (step.value == 3) configurePuntoEncuentro(e);
};

const guardar = async () => {
  await saveTerritorio();
  step.value = 0;
};

const cancel = () => {
  terr.clear();
  step.value = 0;
  route.$patch({ page: "listaTerritorio" });
};

let activeLimitIndex = null;

const configureLimit = async (data) => {
  console.log("configureLimit", data);

  if (polTerr) map().removeLayer(polTerr);

  if (data) {
    if (activeLimitIndex !== null) {
      const limits = [...terr.limits];
      // replace activeLimitIndex
      // limits[activeLimitIndex] = [data.latlng.lat, data.latlng.lng];

      // add after activeLimitIndex
      limits.splice(activeLimitIndex + 1, 0, [data.latlng.lat, data.latlng.lng]);
      terr.$patch({ limits });
      // configureLimit();
      // return;
      activeLimitIndex = activeLimitIndex + 1;
    } else {
      terr.addLimit([data.latlng.lat, data.latlng.lng]);
    }
  }

  if (limitMarkers.length > 0) {
    for (let i in limitMarkers) {
      // console.log("remove", limitMarkers[i]);
      const marker = limitMarkers[i];
      map().removeLayer(marker);
    }
  }
  const markerIcon = m().L.icon({
    iconUrl: `marker-icon.png`,
    iconAnchor: [12, 41],
    iconSize: [25, 41],
  });

  for (let i in terr.limits) {
    const marker = m().L.marker({
        lat: terr.limits[i][0],
        lng: terr.limits[i][1],
      }, {
        icon: markerIcon,
        draggable: true
      }).addTo(map())

    let index = i;

    marker.on("dragend", (e) => {
      // console.log("dragend", e.target.getLatLng());
      let latlng = e.target.getLatLng();
      const limits = [...terr.limits];
      limits[index] = [latlng.lat, latlng.lng];
      terr.$patch({ limits });
      configureLimit();
    });
    marker.on('click', function (e) {
        activeLimitIndex = terr.limits.findIndex((l) => l[0] === e.latlng.lat && l[1] === e.latlng.lng);
    });
    marker.on('dblclick', function (e) {
        // console.log("dblclick", e)
        // console.log("contextmenu", e)
        let latlng = e.latlng;
        const limits = [...terr.limits];
        let indexRemove = limits.findIndex((l) => l[0] === latlng.lat && l[1] === latlng.lng);
        // remove point
        limits.splice(indexRemove, 1);
        terr.$patch({ limits });
        configureLimit();
    })
    limitMarkers.push(marker);
  }

  if (terr.limits.length > 0)
    polTerr = m()
      .L.polyline([...terr.limits, terr.limits[0]], m().sectorOption)
      .addTo(map());
};

const dragEndMz = (data) => {
  const { e, i} = data;
  console.log("dragEndMz", e);
  const latlng = e.target.getLatLng();
  const list = [...terr.mzNumbers];
  list[Number(i)] = [latlng, i];
  terr.$patch({ mzNumbers: list });
  mzNumbers.setMzNumbers(list);
};

const removeMz = (data) => {
  console.log("removeMz", data);
  const { e, i} = data;
  // const latlng = e.latlng;
  let list = [...terr.mzNumbers];
  list.splice(Number(i), 1);
  terr.$patch({ mzNumbers: list });
  mzNumbers.setMzNumbers(list);
  
};

const configureMzNumber = (data) => {
  if (data) terr.addMzNumber([data.latlng, terr.mzNumbers.length]);
  console.log(terr.mzNumbers);
  mzNumbers.setMzNumbers(terr.mzNumbers);
};

const zoomstart = () => {
  if (polTerr) map().removeLayer(polTerr);
};
const zoomend = () => {
  configureLimit();
};
const onMapMoveend = (e) => {
  console.log("onMapMoveend", mapBase.value.m, m().getCenter());
  terr.$patch({ center: m().getCenter() });
};

const ready = () => {
  mzNumbers.setMzNumbers([]);
  // console.log("readyyyy", terr.mzNumbers)
  configureLimit();
  if (terr.mzNumbers) mzNumbers.setMzNumbers(terr.mzNumbers);
  renderPuntoEncuentro();
};

window.addEventListener("storage", function (e) {
  if (e.storageArea === sessionStorage && e.key == "center") {
    //  console.log('change', e.newValue);
    terr.$patch({ center: JSON.parse(e.newValue) });
  }
  // else, event is caused by an update to localStorage, ignore it
});

const configurePuntoEncuentro = async (data) => {
  // console.log("dconfigurePuntoEncuentro", data.latlng)

  let address = await getStreetName(data.latlng);
  const a = address?.address
  const direccion = (a?.house_number ? `${a.road} ${a.house_number}`.trim() : null)
    ?? address?.intersection
    ?? a?.road
    ?? address?.name
    ?? address?.display_name?.split(',')[0]
    ?? ''

  terr.addPuntoEncuentro({
    name: "",
    direccion,
    latlng: data.latlng,
  });

  renderPuntoEncuentro();
};

const puntosEncuentroMarkers = [];

function renderPuntoEncuentro() {
  for (let i in puntosEncuentroMarkers) {
    map().removeLayer(puntosEncuentroMarkers[i]);
  }
  let list = [];
  const icon = L.icon({
    iconUrl: `/pe.png`,
    iconSize: [32, 32],
    alt: "Punto de Encuentro",
  });
  for (let i in terr.puntoEncuentro) {
    const marker = L.marker(terr.puntoEncuentro[i].latlng, { 
      icon,
      draggable: true
     }).addTo(map());

    let index = i;

    marker.on('click', function (e) {
      // console.log("click", e)
      let latlng = e.latlng;
      let indexRemove = terr.puntoEncuentro.findIndex((l) => l.latlng.lat === latlng.lat && l.latlng.lng === latlng.lng);
      // remove point
      terr.puntoEncuentro.splice(indexRemove, 1);
      renderPuntoEncuentro();
    })

    marker.on('dragend', function (e) {
      let latlng = e.target.getLatLng();
      let puntoEncuentro = [...terr.puntoEncuentro];
      puntoEncuentro[index].latlng = latlng;
      terr.$patch({ puntoEncuentro });
      renderPuntoEncuentro();
    });

    puntosEncuentroMarkers.push(marker);
  }
}

async function getStreetName(latlng) {
  return reverseGeocode(latlng)
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.editor-tarjeta {
  height: 100vh;
}

.editor-form {
  width: 40%;
  max-width: 620px;
  min-width: 360px;
  height: 100%;
}

.editor-map {
  flex: 1 1 auto;
  height: 100%;
  border-left: 1px solid var(--surface-border, #e5e7eb);
}

.map-toolbar {
  height: 44px;
  flex: none;
  background: var(--accent);
  color: #fff;
}

.editor-map .map {
  min-height: 0;
}

.section-title {
  font-weight: 600;
  font-size: 1.05rem;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--surface-border, #e5e7eb);
}
</style>
